import React, { useRef, useEffect } from "react";
import "./EditableMessageBox.css";
import DOMPurify from "dompurify";


const EditableMessageBox = ({
    value = "",
    onChange,
    maxLength = 5000,
    className = "",
    onSave,
    onCancel,
    saveLabel = "Save",
    cancelLabel = "Cancel",
}) => {
    const editableRef = useRef(null);
    const isInternalChangeRef = useRef(false);
    const [isLimitExceeded, setIsLimitExceeded] = React.useState(false);

    // Sync value from parent when component is not focused
    useEffect(() => {
        if (!isInternalChangeRef.current && editableRef.current) {
            const currentContent = editableRef.current.innerHTML || "";
            if (currentContent !== value) {
                // Save cursor position before updating
                const selection = window.getSelection();
                let savedRange = null;
                if (selection.rangeCount > 0 && editableRef.current.contains(selection.anchorNode)) {
                    savedRange = selection.getRangeAt(0).cloneRange();
                }

                editableRef.current.innerHTML = DOMPurify.sanitize(value || "", {
                    ALLOWED_TAGS: ["strong", "b", "table", "thead", "tbody", "tr", "td", "th", "br"],
                    ALLOWED_ATTR: ["border", "style"],
                });

                // Restore cursor position after update
                if (savedRange) {
                    try {
                        const newRange = document.createRange();
                        const textNodes = getTextNodes(editableRef.current);
                        if (textNodes.length > 0) {
                            const targetNode = textNodes[0];
                            const offset = Math.min(savedRange.startOffset, targetNode.textContent.length);
                            newRange.setStart(targetNode, offset);
                            newRange.setEnd(targetNode, offset);
                            selection.removeAllRanges();
                            selection.addRange(newRange);
                        } else {
                            newRange.selectNodeContents(editableRef.current);
                            newRange.collapse(false);
                            selection.removeAllRanges();
                            selection.addRange(newRange);
                        }
                    } catch (err) {
                        const range = document.createRange();
                        range.selectNodeContents(editableRef.current);
                        range.collapse(false);
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                } else {
                    const range = document.createRange();
                    range.selectNodeContents(editableRef.current);
                    range.collapse(false);
                    selection.removeAllRanges();
                    selection.addRange(range);
                }
            }
            if (maxLength && editableRef.current) {
                const text = editableRef.current.textContent || "";
                setIsLimitExceeded(text.length >= maxLength);
            }
        }
        isInternalChangeRef.current = false;
    }, [value, maxLength]);

    // Helper function to get text nodes
    const getTextNodes = (node) => {
        const textNodes = [];
        const walker = document.createTreeWalker(
            node,
            NodeFilter.SHOW_TEXT,
            null,
            false
        );
        let textNode;
        while ((textNode = walker.nextNode())) {
            textNodes.push(textNode);
        }
        return textNodes;
    };

    // Normalize pasted content - tables and bold stay as HTML, everything else becomes plain text
    const handlePaste = (e) => {
        e.preventDefault();

        const clipboardData = e.clipboardData || window.clipboardData;
        const htmlData = clipboardData.getData("text/html");
        const textData = clipboardData.getData("text/plain");

        let cleanContent = "";
        let hasHtmlContent = false;

        if (htmlData) {
            const tempDiv = document.createElement("div");
            tempDiv.innerHTML = DOMPurify.sanitize(htmlData);

            // Remove unwanted elements
            const unwantedElements = tempDiv.querySelectorAll("img, picture, figure, svg, canvas, video, audio, iframe, embed, object, script, style");
            unwantedElements.forEach((el) => el.remove());

            // Process tables - clean and add borders
            const tables = tempDiv.querySelectorAll("table");
            tables.forEach((table) => {
                table.removeAttribute("class");
                table.removeAttribute("bgcolor");
                table.removeAttribute("background");
                table.setAttribute("border", "1");
                table.setAttribute("style", "border-collapse: collapse;");

                table.querySelectorAll("*").forEach((el) => {
                    el.removeAttribute("class");
                    el.removeAttribute("bgcolor");
                    el.removeAttribute("background");

                    if (el.tagName === "TD" || el.tagName === "TH") {
                        el.setAttribute("style", "border: 1px solid black; padding: 4px 8px;");
                    } else {
                        el.removeAttribute("style");
                    }
                });
            });

            // Convert font-weight styles to <strong> tags
            const elementsWithBold = tempDiv.querySelectorAll("[style*='font-weight']");
            elementsWithBold.forEach((el) => {
                const style = el.getAttribute("style") || "";
                const fontWeight = style.match(/font-weight\s*:\s*(\d+|bold|bolder)/i);
                if (fontWeight) {
                    const weight = fontWeight[1].toLowerCase();
                    if (weight === "bold" || weight === "bolder" || parseInt(weight) >= 600) {
                        const strong = document.createElement("strong");
                        strong.innerHTML = DOMPurify.sanitize(el.innerHTML);
                        el.innerHTML = "";
                        el.appendChild(strong);
                    }
                }
            });

            // Unwrap non-allowed container elements while preserving tables and bold
            const allowedTags = ["STRONG", "B", "TABLE", "THEAD", "TBODY", "TR", "TD", "TH"];

            const unwrapElement = (el) => {
                const parent = el.parentNode;
                if (!parent) return;

                const fragment = document.createDocumentFragment();
                while (el.firstChild) {
                    fragment.appendChild(el.firstChild);
                }

                parent.insertBefore(fragment, el);
                parent.removeChild(el);
            };

            let changed = true;
            let iterations = 0;
            const maxIterations = 100;

            while (changed && iterations < maxIterations) {
                changed = false;
                iterations++;

                const allElements = Array.from(tempDiv.querySelectorAll("*"));

                for (let i = allElements.length - 1; i >= 0; i--) {
                    const el = allElements[i];
                    const tagName = el.tagName.toUpperCase();

                    if (allowedTags.includes(tagName) || el === tempDiv) continue;

                    unwrapElement(el);
                    changed = true;
                    break;
                }
            }

            const remainingElements = tempDiv.querySelectorAll("*");
            remainingElements.forEach((el) => {
                const tagName = el.tagName.toUpperCase();
                if (!allowedTags.includes(tagName) && el !== tempDiv) {
                    const attributes = Array.from(el.attributes);
                    attributes.forEach((attr) => {
                        if (attr.name !== "href") {
                            el.removeAttribute(attr.name);
                        }
                    });
                }
            });

            const hasTables = tempDiv.querySelectorAll("table").length > 0;
            const hasBold = tempDiv.querySelectorAll("strong, b").length > 0;

            if (hasTables || hasBold) {
                hasHtmlContent = true;
                cleanContent = tempDiv.innerHTML;
            } else {
                cleanContent = textData;
            }
        } else {
            cleanContent = textData;
        }

        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);

            if (maxLength) {
                const currentText = editableRef.current.textContent || "";
                const selectedText = selection.toString();
                const availableSpace = maxLength - (currentText.length - selectedText.length);

                if (availableSpace <= 0) {
                    setIsLimitExceeded(true);
                    return;
                }

                const tempDivForLength = document.createElement("div");
                tempDivForLength.innerHTML = DOMPurify.sanitize(cleanContent);
                const pasteTextLength = tempDivForLength.textContent.length;

                if (pasteTextLength > availableSpace) {
                    if (hasHtmlContent) {
                        const textToPaste = tempDivForLength.textContent.substring(0, availableSpace);
                        cleanContent = textToPaste;
                        hasHtmlContent = false;
                        setIsLimitExceeded(true);
                    } else {
                        cleanContent = cleanContent.substring(0, availableSpace);
                        setIsLimitExceeded(true);
                    }
                }
            }

            range.deleteContents();

            if (hasHtmlContent) {
                const sanitizedHtml = DOMPurify.sanitize(cleanContent, {
                    ALLOWED_TAGS: ["strong", "b", "table", "thead", "tbody", "tr", "td", "th", "br"],
                    ALLOWED_ATTR: ["border", "style"],
                });
                const fragment = document.createRange().createContextualFragment(sanitizedHtml);
                range.insertNode(fragment);
            } else {
                const textNode = document.createTextNode(cleanContent);
                range.insertNode(textNode);
            }

            range.collapse(false);
            selection.removeAllRanges();
            selection.addRange(range);
        }

        isInternalChangeRef.current = true;
        const html = editableRef.current.innerHTML || "";
        if (onChange) {
            onChange(html);
        }
        if (maxLength) {
            const text = editableRef.current.textContent || "";
            setIsLimitExceeded(text.length >= maxLength);
        }
    };

    const cleanupDivs = (element) => {
        const divs = Array.from(element.querySelectorAll("div"));
        divs.forEach((div) => {
            if (!div.textContent.trim() && div.children.length === 0) {
                const textNode = document.createTextNode("\n");
                div.parentNode?.replaceChild(textNode, div);
            }
        });
    };

    const handleInput = (e) => {
        isInternalChangeRef.current = true;
        const html = e.currentTarget.innerHTML || "";
        if (onChange) {
            onChange(html);
        }
        if (maxLength) {
            const text = e.currentTarget.textContent || "";
            setIsLimitExceeded(text.length >= maxLength);
        }
    };

    const handleBlur = () => {
        if (editableRef.current) {
            cleanupDivs(editableRef.current);
            const html = editableRef.current.innerHTML || "";
            if (onChange) {
                onChange(html);
            }
            if (maxLength) {
                const text = editableRef.current.textContent || "";
                setIsLimitExceeded(text.length >= maxLength);
            }
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const selection = window.getSelection();

            if (selection.rangeCount > 0) {
                const range = selection.getRangeAt(0);

                let container = range.commonAncestorContainer;
                if (container.nodeType === Node.TEXT_NODE) {
                    container = container.parentElement;
                }

                if (container && container.tagName === "DIV" && container !== editableRef.current && !container.textContent.trim()) {
                    const textNode = document.createTextNode("\n");
                    container.parentNode?.replaceChild(textNode, container);
                    range.setStartAfter(textNode);
                    range.collapse(true);
                } else {
                    const textNode = document.createTextNode("\n");
                    range.deleteContents();
                    range.insertNode(textNode);
                    range.setStartAfter(textNode);
                    range.collapse(true);
                }

                selection.removeAllRanges();
                selection.addRange(range);

                setTimeout(() => {
                    isInternalChangeRef.current = true;
                    const html = editableRef.current?.innerHTML || "";
                    if (onChange) {
                        onChange(html);
                    }
                    if (maxLength) {
                        const text = editableRef.current.textContent || "";
                        setIsLimitExceeded(text.length >= maxLength);
                    }
                }, 0);
            }
            return;
        }

        if (maxLength) {
            const text = editableRef.current.textContent || "";
            const isAllowedKey = [
                "Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown",
                "Home", "End", "PageUp", "PageDown", "Tab",
            ].includes(e.key);

            if (!isAllowedKey && !e.metaKey && !e.ctrlKey && !e.altKey && text.length >= maxLength) {
                const selection = window.getSelection();
                const selectedText = selection.toString();

                if (selectedText.length === 0) {
                    e.preventDefault();
                    setIsLimitExceeded(true);
                }
            }
        }
    };

    // Sanitize text for validation - remove HTML tags to check if it's truly empty
    const sanitizedText = DOMPurify.sanitize(value || "", { ALLOWED_TAGS: [] }).replace(/&nbsp;/g, " ").trim();
    const isSaveDisabled = !sanitizedText || isLimitExceeded;

    return (
        <>
            <div className="editable-message-container">
                <div
                    ref={editableRef}
                    className={`editable-message-box ${className} ${isLimitExceeded ? "limit-exceeded" : ""}`}
                    contentEditable
                    suppressContentEditableWarning
                    onInput={handleInput}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                    onBlur={handleBlur}
                />
            </div>

            <div className="edit-box-footer">
                <div className="edit-footer-info">
                    {isLimitExceeded && (
                        <span className="limit-error-message">Character Count Exceeded</span>
                    )}
                </div>
                <div className="edit-buttons-container">
                    <button
                        className="button-style primary-grey-button cancel-button"
                        onClick={onCancel}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        className={`button-style primary-button save-button ${isSaveDisabled ? "disabled-button" : ""}`}
                        onClick={onSave}
                        disabled={isSaveDisabled}
                    >
                        {saveLabel}
                    </button>
                </div>
            </div>
        </>
    );
};

export default EditableMessageBox;

